import React, { useState, useMemo, useEffect } from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Select from '../../components/ui/Select';
import TruckCard from './components/TruckCard';
import { setTruckStatus, subscribeTrucks } from '../../data/trucksData';
import { useAuth } from '../../contexts/AuthContext';
import maskVehicleNumber from '../../utils/maskVehicleNumber';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

// Factory pincodes (approximate)
const factoryPincodes = {
  'KSSK, Kopargaon, Maharashtra': '423601',
  'PVSSK, Pravara, Maharashtra': '413713',
  'BSSK, Bhima, Pune': '412205',
  'WSSSK, Warana, Kolhapur': '416113',
  'NSSSK, Nira Bhima, Solapur': '413102',
  'RSSSK, Rajaram, Kolhapur': '416004',
  'DSSSK, Datta, Nashik': '422401',
  'MSSSK, Malegaon, Nashik': '423203',
  'VNSSK, Vitthalrao Nagar, Solapur': '413004',
  'SCSSK, Shri Chhatrapati, Aurangabad': '431001',
};

const factoryOptions = Object.keys(factoryPincodes)?.map((name) => ({
  value: name,
  label: name,
}));

const capacityOptions = [
  { value: 'all', label: 'All Capacities' },
  { value: '0-20', label: 'Up to 20 MT' },
  { value: '20-35', label: '20 – 35 MT (Average Truck Capacity 25T to 35T)' },
  { value: '35+', label: '35+ MT' },
];

const locationOptions = [
  { value: 'all', label: 'All States' },
  { value: 'Maharashtra', label: 'Maharashtra' },
  { value: 'Gujarat', label: 'Gujarat' },
  { value: 'Uttar Pradesh', label: 'Uttar Pradesh' },
  { value: 'Karnataka', label: 'Karnataka' },
  { value: 'Tamil Nadu', label: 'Tamil Nadu' },
  { value: 'Rajasthan', label: 'Rajasthan' },
  { value: 'Madhya Pradesh', label: 'Madhya Pradesh' },
  { value: 'West Bengal', label: 'West Bengal' },
];

const availabilityOptions = [
  { value: 'all', label: 'All' },
  { value: 'Available', label: 'Available' },
  { value: 'Busy', label: 'Busy' },
];

// Booking Modal with Route Step + Multi-destination
const BookingRouteModal = ({ truck, onClose, onBooked }) => {
  const [step, setStep] = useState(1);
  const [loadingFactory, setLoadingFactory] = useState('');
  const [routeError, setRouteError] = useState('');
  const [booked, setBooked] = useState(false);
  const [destinations, setDestinations] = useState([{ id: 1, city: '', pincode: '', trucks: 1 }]);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();

  const fromPincode = loadingFactory ? (factoryPincodes?.[loadingFactory] || '') : '';

  const addDestination = () => {
    setDestinations(prev => [...prev, { id: Date.now(), city: '', pincode: '', trucks: 1 }]);
  };

  const removeDestination = (id) => {
    if (destinations?.length <= 1) return;
    setDestinations(prev => prev?.filter(d => d?.id !== id));
  };

  const fetchDistanceForDest = async (id, destPin) => {
    if (!fromPincode || fromPincode?.length !== 6 || destPin?.length !== 6) return;
    setDestinations(prev => prev?.map(d => d?.id === id ? { ...d, distLoading: true, lockedDist: null } : d));
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const resp = await fetch(`${supabaseUrl}/functions/v1/calculate-route-distance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ originPincode: fromPincode, destinationPincode: destPin }),
      });
      if (resp?.ok) {
        const data = await resp.json();
        setDestinations(prev => prev?.map(d => d?.id === id ? { ...d, lockedDist: data?.distanceKm, distLoading: false } : d));
      } else {
        setDestinations(prev => prev?.map(d => d?.id === id ? { ...d, lockedDist: null, distLoading: false } : d));
      }
    } catch {
      setDestinations(prev => prev?.map(d => d?.id === id ? { ...d, lockedDist: null, distLoading: false } : d));
    }
  };

  const updateDestination = (id, field, value) => {
    setDestinations(prev => prev?.map(d => {
      if (d?.id !== id) return d;
      const updated = { ...d, [field]: value };
      // When pincode changes and is valid 6-digit, fetch real road distance
      if (field === 'pincode') {
        const pin = value?.replace(/\D/g, '')?.substring(0, 6);
        updated.pincode = pin;
        updated.lockedDist = null;
        updated.distLoading = false;
        if (pin?.length === 6) fetchDistanceForDest(id, pin);
      }
      return updated;
    }));
  };

  const totalTrucks = destinations?.reduce((sum, d) => sum + (parseInt(d?.trucks) || 0), 0);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })?.format(amount);

  const isPincodeValid = (pincode) => /^\d{6}$/?.test(pincode);

  const handleNext = () => {
    if (!loadingFactory) { setRouteError('Please select a loading factory.'); return; }
    const invalidDest = destinations?.find(d => !isPincodeValid(d?.pincode));
    if (invalidDest) { setRouteError('Please enter a valid 6-digit pincode for every destination.'); return; }
    setRouteError('');
    // Check login + role before proceeding to review — truck booking is
    // buyer-only; transporter/factory roles must not book trucks
    if (!user || !user?.id || !['buyer', 'admin'].includes(userProfile?.role)) {
      setShowGuestModal(true);
      return;
    }
    setStep(2);
  };

  const handleConfirm = () => {
    setTruckStatus(truck?.id, 'Busy');
    setBooked(true);
    if (onBooked) onBooked(truck?.id);
  };

  // Approx rate per kg from ratePerTon stored on truck
  // truck.ratePerTon is stored in ₹/Ton; convert to ₹/Kg
  const approxRatePerKg = truck?.ratePerTon ? (truck?.ratePerTon / 1000) : (truck?.ratePerKg || null);

  // Modern Booking Confirmation Popup
  if (booked) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
        <div className="rounded-3xl shadow-2xl w-full max-w-md overflow-hidden" style={{ backgroundColor: '#ffffff' }}>
          {/* Green header band */}
          <div className="px-8 pt-8 pb-6 text-center" style={{ background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)' }}>
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '3px solid rgba(255,255,255,0.4)' }}>
              <Icon name="CheckCircle" size={40} color="#ffffff" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-1">Booking Confirmed!</h2>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.85)' }}>Your truck booking request has been submitted</p>
          </div>

          {/* Booking summary */}
          <div className="px-6 py-5">
            <div className="rounded-2xl p-4 mb-4 space-y-3" style={{ backgroundColor: '#f0fdf4', border: '1.5px solid #bbf7d0' }}>
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#065f46' }}>Booking Summary</p>
              <div className="flex justify-between items-center text-sm">
                <span className="flex items-center gap-1.5" style={{ color: '#6b7280' }}>
                  <Icon name="Truck" size={13} color="#9ca3af" /> Vehicle
                </span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>{maskVehicleNumber(truck?.vehicleNumber, user)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="flex items-center gap-1.5" style={{ color: '#6b7280' }}>
                  <Icon name="Package" size={13} color="#9ca3af" /> Capacity
                </span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>{truck?.capacityMT} MT</span>
              </div>
              <div className="flex justify-between items-start text-sm">
                <span className="flex items-center gap-1.5" style={{ color: '#6b7280' }}>
                  <Icon name="MapPin" size={13} color="#9ca3af" /> Loading From
                </span>
                <span className="font-semibold text-right" style={{ color: '#1f2937', maxWidth: '55%' }}>{loadingFactory?.split(',')?.[0]}</span>
              </div>
              <div className="border-t pt-3 space-y-2" style={{ borderColor: '#bbf7d0' }}>
                <p className="text-xs font-semibold" style={{ color: '#065f46' }}>Destinations</p>
                {destinations?.filter(d => d?.pincode)?.map((d, i) => (
                  <div key={d?.id} className="flex justify-between items-center text-xs">
                    <span style={{ color: '#6b7280' }}>{d?.city || `Destination ${i + 1}`} — {d?.pincode}</span>
                    <div className="flex items-center gap-2">
                      {d?.lockedDist && <span className="font-medium" style={{ color: '#059669' }}>{d?.lockedDist} km</span>}
                      <span className="font-semibold" style={{ color: '#1f2937' }}>{d?.trucks} truck{d?.trucks > 1 ? 's' : ''}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center text-sm border-t pt-3" style={{ borderColor: '#bbf7d0' }}>
                <span style={{ color: '#6b7280' }}>Total Trucks</span>
                <span className="font-bold text-base" style={{ color: '#059669' }}>{totalTrucks} trucks</span>
              </div>
              {truck?.ratePerTon && (
                <div className="flex justify-between items-center text-sm">
                  <span style={{ color: '#6b7280' }}>Rate per Ton</span>
                  <span className="font-bold" style={{ color: '#1f2937' }}>₹{Number(truck?.ratePerTon)?.toLocaleString('en-IN')}/Ton</span>
                </div>
              )}
            </div>

            <div className="rounded-xl p-3 mb-5 flex items-center gap-2" style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>
              <Icon name="Clock" size={14} color="#d97706" />
              <p className="text-xs" style={{ color: '#92400e' }}>Our team will confirm your booking within <strong>2 hours</strong></p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => navigate('/buyer-operations-dashboard')}
                className="py-3 rounded-xl text-sm font-bold border-2 transition-all"
                style={{ borderColor: '#059669', color: '#059669', backgroundColor: '#f0fdf4' }}
              >
                View Booking
              </button>
              <button
                onClick={() => navigate('/live-shipment-tracking')}
                className="py-3 rounded-xl text-sm font-bold text-white transition-all"
                style={{ backgroundColor: '#059669' }}
              >
                Track Shipment
              </button>
            </div>
            <button
              onClick={onClose}
              className="w-full mt-3 py-2 text-xs font-medium rounded-xl"
              style={{ color: '#9ca3af', backgroundColor: '#f9fafb' }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Guest Login Modal */}
      {showGuestModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="rounded-2xl shadow-2xl w-full max-w-sm p-7 text-center" style={{ backgroundColor: '#ffffff' }}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#fef3c7' }}>
              <Icon name="LogIn" size={26} color="#d97706" />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#1f2937' }}>Login Required</h3>
            <p className="text-sm mb-6" style={{ color: '#6b7280' }}>
              Please login or register to complete your booking.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowGuestModal(false); onClose(); navigate('/login-authentication'); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold"
                style={{ backgroundColor: '#d97706', color: '#ffffff' }}
              >
                Login
              </button>
              <button
                onClick={() => { setShowGuestModal(false); onClose(); navigate('/user-registration'); }}
                className="flex-1 py-2.5 rounded-xl border text-sm font-bold"
                style={{ borderColor: '#d97706', color: '#d97706', backgroundColor: '#fffbeb' }}
              >
                Register
              </button>
            </div>
            <button
              onClick={() => setShowGuestModal(false)}
              className="mt-4 text-xs underline"
              style={{ color: '#9ca3af' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
        <div className="rounded-2xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]" style={{ backgroundColor: '#ffffff' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: '#f0e8de' }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fef3c7' }}>
                <Icon name="Truck" size={18} color="#d97706" />
              </div>
              <div>
                <h2 className="text-base font-bold" style={{ color: '#1f2937' }}>Book This Truck</h2>
                <p className="text-xs" style={{ color: '#9ca3af' }}>{maskVehicleNumber(truck?.vehicleNumber, user)} · {truck?.capacityMT} MT</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100">
              <Icon name="X" size={18} color="#6b7280" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* Step indicator */}
            <div className="flex items-center gap-2 mb-5">
              <div className="flex items-center gap-1.5">
                <span className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center"
                  style={{ backgroundColor: step >= 1 ? '#d97706' : '#e5d9cc', color: step >= 1 ? '#ffffff' : '#9ca3af' }}>1</span>
                <span className="text-xs font-medium" style={{ color: step >= 1 ? '#d97706' : '#9ca3af' }}>Route & Destinations</span>
              </div>
              <div className="flex-1 h-px" style={{ backgroundColor: '#e5d9cc' }} />
              <div className="flex items-center gap-1.5">
                <span className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center"
                  style={{ backgroundColor: step >= 2 ? '#d97706' : '#e5d9cc', color: step >= 2 ? '#ffffff' : '#9ca3af' }}>2</span>
                <span className="text-xs font-medium" style={{ color: step >= 2 ? '#d97706' : '#9ca3af' }}>Confirm & Rate</span>
              </div>
            </div>

            {step === 1 && (
              <div className="space-y-4">
                <p className="text-sm" style={{ color: '#6b7280' }}>
                  Select loading factory and add one or more destinations with truck quantity for each.
                </p>
                <Select
                  label="Loading Factory"
                  options={factoryOptions}
                  value={loadingFactory}
                  onChange={setLoadingFactory}
                  placeholder="Select loading factory"
                />

                {/* Multi-destination */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium" style={{ color: '#374151' }}>Destinations & Truck Split</label>
                    <button
                      onClick={addDestination}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold"
                      style={{ backgroundColor: '#d97706', color: '#ffffff' }}
                    >
                      + Add
                    </button>
                  </div>
                  <div className="space-y-3">
                    {destinations?.map((dest, index) => {
                      const dist = dest?.lockedDist || null;
                      const ratePerTon = truck?.ratePerTon || null;
                      const pincodeValid = isPincodeValid(dest?.pincode);
                      return (
                        <div key={dest?.id} className="rounded-xl border p-3 space-y-2" style={{ backgroundColor: '#faf7f2', borderColor: '#e5d9cc' }}>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold" style={{ color: '#92400e' }}>Destination {index + 1}</span>
                            {destinations?.length > 1 && (
                              <button onClick={() => removeDestination(dest?.id)} className="text-xs" style={{ color: '#dc2626' }}>Remove</button>
                            )}
                          </div>
                          {/* Destination / City (optional) */}
                          <div>
                            <label className="text-xs font-medium block mb-1" style={{ color: '#374151' }}>Destination / City <span className="font-normal" style={{ color: '#9ca3af' }}>(optional)</span></label>
                            <input
                              type="text"
                              value={dest?.city}
                              onChange={e => updateDestination(dest?.id, 'city', e?.target?.value)}
                              placeholder="e.g. Mumbai, Pune..."
                              className="w-full rounded-lg border px-2 py-1.5 text-xs"
                              style={{ borderColor: '#e5d9cc', backgroundColor: '#ffffff', color: '#1f2937' }}
                            />
                          </div>
                          {/* Pincode (mandatory) + Trucks */}
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs font-medium block mb-1" style={{ color: '#374151' }}>
                                Pincode <span style={{ color: '#dc2626' }}>*</span>
                              </label>
                              <input
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={dest?.pincode}
                                onChange={e => updateDestination(dest?.id, 'pincode', e?.target?.value)}
                                placeholder="6-digit pincode"
                                className="w-full rounded-lg border px-2 py-1.5 text-xs font-semibold"
                                style={{
                                  borderColor: dest?.pincode && !pincodeValid ? '#dc2626' : pincodeValid ? '#059669' : '#e5d9cc',
                                  backgroundColor: '#ffffff',
                                  color: '#1f2937'
                                }}
                              />
                              {dest?.pincode && !pincodeValid && (
                                <p className="text-[9px] mt-0.5" style={{ color: '#dc2626' }}>Enter valid 6-digit pincode</p>
                              )}
                            </div>
                            <div>
                              <label className="text-xs font-medium block mb-1" style={{ color: '#374151' }}>Trucks</label>
                              <input
                                type="number"
                                min="1"
                                max="50"
                                value={dest?.trucks}
                                onChange={e => updateDestination(dest?.id, 'trucks', e?.target?.value)}
                                className="w-full rounded-lg border px-2 py-1.5 text-xs font-semibold"
                                style={{ borderColor: '#e5d9cc', backgroundColor: '#ffffff', color: '#1f2937' }}
                              />
                            </div>
                          </div>
                          {pincodeValid && dest?.distLoading && (
                            <div className="flex items-center gap-2 py-1">
                              <div className="w-3 h-3 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
                              <p className="text-[10px]" style={{ color: '#9ca3af' }}>Calculating road distance...</p>
                            </div>
                          )}
                          {pincodeValid && !dest?.distLoading && dist !== null && (
                            <div className="grid grid-cols-2 gap-1.5">
                              <div className="rounded p-1.5 text-center" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                                <p className="text-[9px]" style={{ color: '#9ca3af' }}>Road Distance</p>
                                <p className="text-xs font-bold" style={{ color: '#059669' }}>{dist} km</p>
                              </div>
                              <div className="rounded p-1.5 text-center" style={{ backgroundColor: '#ffffff', border: '1px solid #f0e8de' }}>
                                <p className="text-[9px]" style={{ color: '#9ca3af' }}>Rate per Ton</p>
                                <p className="text-xs font-bold" style={{ color: '#d97706' }}>{ratePerTon ? `₹${Number(ratePerTon)?.toLocaleString('en-IN')}` : '—'}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {routeError && (
                  <p className="text-xs" style={{ color: '#dc2626' }}>{routeError}</p>
                )}
                <button
                  onClick={handleNext}
                  className="w-full py-2.5 rounded-lg text-sm font-bold mt-2"
                  style={{ backgroundColor: '#d97706', color: '#ffffff' }}
                >
                  Next: Review & Confirm →
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="rounded-lg p-4 space-y-2.5" style={{ backgroundColor: '#f8f1e9' }}>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: '#6b7280' }}>Vehicle Number</span>
                    <span className="font-semibold" style={{ color: '#1f2937' }}>{maskVehicleNumber(truck?.vehicleNumber, user)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: '#6b7280' }}>Capacity</span>
                    <span className="font-semibold" style={{ color: '#1f2937' }}>{truck?.capacityMT} MT</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: '#6b7280' }}>Loading From</span>
                    <span className="font-semibold text-right" style={{ color: '#1f2937', maxWidth: '60%' }}>{loadingFactory?.split(',')?.[0]}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: '#6b7280' }}>Total Trucks</span>
                    <span className="font-bold" style={{ color: '#d97706' }}>{totalTrucks}</span>
                  </div>
                  <div className="pt-2 border-t" style={{ borderColor: '#e5d9cc' }}>
                    <p className="text-xs font-semibold mb-2" style={{ color: '#374151' }}>Destination Breakdown</p>
                    {destinations?.filter(d => d?.pincode)?.map((d, i) => (
                      <div key={d?.id} className="flex justify-between text-xs py-1">
                        <span style={{ color: '#6b7280' }}>{d?.city || `Destination ${i + 1}`} — {d?.pincode} ({d?.trucks} truck{d?.trucks > 1 ? 's' : ''})</span>
                        <span className="font-medium" style={{ color: '#059669' }}>{d?.lockedDist ? `${d?.lockedDist} km` : '—'}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between text-sm pt-2 border-t" style={{ borderColor: '#e5d9cc' }}>
                    <span style={{ color: '#6b7280' }}>Rate per Ton</span>
                    <span className="font-bold" style={{ color: '#1f2937' }}>
                      {truck?.ratePerTon ? `₹${Number(truck?.ratePerTon)?.toLocaleString('en-IN')}/Ton` : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: '#6b7280' }}>Approx Rate per Kg</span>
                    <span className="font-bold text-base" style={{ color: '#d97706' }}>
                      {approxRatePerKg ? `₹${approxRatePerKg?.toFixed(2)}/Kg` : '—'}
                    </span>
                  </div>
                </div>

                <div className="rounded-lg p-3 flex items-start gap-2" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
                  <Icon name="Info" size={14} color="#d97706" className="mt-0.5 flex-shrink-0" />
                  <p className="text-xs" style={{ color: '#92400e' }}>
                    Confirming this booking will mark the truck as <strong>Busy</strong>. Our team will confirm within 2 hours.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 py-2.5 rounded-lg border text-sm font-medium"
                    style={{ borderColor: '#d1c4b0', color: '#374151' }}
                  >
                    ← Back
                  </button>
                  <button
                    onClick={handleConfirm}
                    className="flex-1 py-2.5 rounded-lg text-sm font-bold"
                    style={{ backgroundColor: '#d97706', color: '#ffffff' }}
                  >
                    Confirm Booking
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const TruckMarketplace = () => {
  const [platformTrucks, setPlatformTrucks] = useState([]);
  const [filters, setFilters] = useState({ capacity: 'all', location: 'all', availability: 'all' });
  const [bookingTruck, setBookingTruck] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const unsub = subscribeTrucks(setPlatformTrucks);
    return unsub;
  }, []);

  const handleFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleBooked = (truckId) => {};

  const handleBookTruck = (truck) => {
    if (!user || !['buyer', 'admin'].includes(userProfile?.role)) { setShowLoginModal(true); return; }
    setBookingTruck(truck);
  };

  const filtered = useMemo(() => {
    return platformTrucks?.filter((truck) => {
      if (filters?.capacity !== 'all') {
        const cap = truck?.capacityMT;
        if (filters?.capacity === '0-20' && cap > 20) return false;
        if (filters?.capacity === '20-35' && (cap < 20 || cap > 35)) return false;
        if (filters?.capacity === '35+' && cap <= 35) return false;
      }
      if (filters?.location !== 'all' && !truck?.currentLocation?.includes(filters?.location)) return false;
      if (filters?.availability !== 'all' && truck?.status !== filters?.availability) return false;
      return true;
    });
  }, [filters, platformTrucks]);

  const capacityOptionsTranslated = [
    { value: 'all', label: t('truckMarketplace.allCapacities', 'All Capacities') },
    { value: '0-20', label: t('truckMarketplace.upTo20MT', 'Up to 20 MT') },
    { value: '20-35', label: 'Average Truck Capacity 25T to 35T' },
    { value: '35+', label: '35+ MT' },
  ];

  const locationOptionsTranslated = [
    { value: 'all', label: t('truckMarketplace.allStates', 'All States') },
    { value: 'Maharashtra', label: 'Maharashtra' },
    { value: 'Gujarat', label: 'Gujarat' },
    { value: 'Uttar Pradesh', label: 'Uttar Pradesh' },
    { value: 'Karnataka', label: 'Karnataka' },
    { value: 'Tamil Nadu', label: 'Tamil Nadu' },
    { value: 'Rajasthan', label: 'Rajasthan' },
    { value: 'Madhya Pradesh', label: 'Madhya Pradesh' },
    { value: 'West Bengal', label: 'West Bengal' },
  ];

  const availabilityOptionsTranslated = [
    { value: 'all', label: t('truckMarketplace.all', 'All') },
    { value: 'Available', label: t('truckMarketplace.available', 'Available') },
    { value: 'Busy', label: t('truckMarketplace.busy', 'Busy') },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#faf7f2' }}>
      <Helmet>
        <title>Truck Marketplace — Find Transporters | SugarHathi</title>
        <meta name="description" content="Browse available trucks and transporters for sugar logistics on SugarHathi's truck marketplace across India." />
      </Helmet>
      <RoleBasedHeader />
      <BreadcrumbNavigation />
      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#d97706' }}>
              <Icon name="Truck" size={22} color="#ffffff" />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: '#1e4a72', fontFamily: 'DM Sans, sans-serif' }}>
                {t('truckMarketplace.title', 'Verified Trucks')}
              </h1>
              <p className="text-sm" style={{ color: '#374151' }}>{t('truckMarketplace.description', 'Verified Vehicles')}</p>
            </div>
          </div>
        </div>

        {/* Advisory Note */}
        <div className="rounded-xl p-4 mb-6 flex items-start gap-3" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
          <Icon name="Info" size={18} color="#d97706" className="mt-0.5 flex-shrink-0" />
          <p className="text-sm" style={{ color: '#92400e' }}>
            <strong>{t('truckMarketplace.tipLabel', 'Tip:')}</strong> {t('truckMarketplace.tipText', 'Best to book truck together with sugar for accurate rate and route. Use the Sugar + Truck option in the Sugar Purchase Order Form for a seamless combined booking.')}
          </p>
        </div>

        {/* Filters */}
        <div className="rounded-xl border p-5 mb-6" style={{ backgroundColor: '#ffffff', borderColor: '#e5d9cc' }}>
          <div className="flex items-center gap-2 mb-4">
            <Icon name="SlidersHorizontal" size={18} color="#d97706" />
            <span className="text-sm font-semibold" style={{ color: '#1e4a72' }}>{t('truckMarketplace.filterTrucks', 'Filter Trucks')}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Select label={t('truckMarketplace.capacityMT', 'Capacity (MT)')} options={capacityOptionsTranslated} value={filters?.capacity} onChange={(val) => handleFilter('capacity', val)} />
            <Select label={t('truckMarketplace.locationState', 'Location (State)')} options={locationOptionsTranslated} value={filters?.location} onChange={(val) => handleFilter('location', val)} />
            <Select label={t('truckMarketplace.availability', 'Availability')} options={availabilityOptionsTranslated} value={filters?.availability} onChange={(val) => handleFilter('availability', val)} />
          </div>
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm" style={{ color: '#374151' }}>
            {t('truckMarketplace.showing', 'Showing')} <span className="font-semibold" style={{ color: '#1e4a72' }}>{filtered?.length}</span> {t('truckMarketplace.trucks', 'truck')}{filtered?.length !== 1 ? 's' : ''}
          </p>
          <span className="text-xs px-3 py-1 rounded-full font-medium" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
            {t('truckMarketplace.platformManaged', 'Platform Managed')} · {t('truckMarketplace.verified', 'Verified')}
          </span>
        </div>

        {/* Truck List */}
        {filtered?.length === 0 ? (
          <div className="text-center py-16 rounded-xl border" style={{ backgroundColor: '#ffffff', borderColor: '#e5d9cc' }}>
            <Icon name="Truck" size={40} color="#d1c4b0" />
            <p className="mt-3 text-base font-medium" style={{ color: '#374151' }}>{t('truckMarketplace.noTrucksMatch', 'No trucks match your filters')}</p>
            <button
              onClick={() => setFilters({ capacity: 'all', location: 'all', availability: 'all' })}
              className="mt-3 text-sm underline"
              style={{ color: '#d97706' }}
            >
              {t('truckMarketplace.clearFilters', 'Clear filters')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered?.map((truck) => (
              <TruckCard key={truck?.id} truck={truck} onBook={handleBookTruck} />
            ))}
          </div>
        )}
      </div>

      {/* Booking Route Modal */}
      {bookingTruck && (
        <BookingRouteModal
          truck={bookingTruck}
          onClose={() => setBookingTruck(null)}
          onBooked={handleBooked}
        />
      )}
      {showLoginModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="rounded-2xl shadow-2xl w-full max-w-sm p-7 text-center" style={{ backgroundColor: '#ffffff' }}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#fef3c7' }}>
              <Icon name="LogIn" size={26} color="#d97706" />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#1f2937' }}>Login Required</h3>
            <p className="text-sm mb-5" style={{ color: '#6b7280' }}>Please login or register to book this truck.</p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowLoginModal(false); navigate('/login-authentication'); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold"
                style={{ backgroundColor: '#d97706', color: '#ffffff' }}
              >
                Login
              </button>
              <button
                onClick={() => { setShowLoginModal(false); navigate('/user-registration'); }}
                className="flex-1 py-2.5 rounded-xl border text-sm font-bold"
                style={{ borderColor: '#d97706', color: '#d97706', backgroundColor: '#fffbeb' }}
              >
                Register
              </button>
            </div>
            <button onClick={() => setShowLoginModal(false)} className="mt-4 text-xs underline" style={{ color: '#9ca3af' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TruckMarketplace;